import { useEffect, useState, useCallback, useRef } from "react";
import { ShieldCheck, CalendarDays, FileWarning, CircleCheck, Upload, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { DocContentView, downloadApiFile } from "@/components/shared/DocKit";

/* ---------- acknowledgement dialog ---------- */
function AckDialog({ doc, onClose, onAcknowledged }) {
  const { t } = useLang();
  const [read, setRead] = useState(false);
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (doc) {
      setRead(false);
      api.get(`/docs/documents/${doc.id}`).then((r) => setFull(r.data)).catch(() => setFull(doc));
    }
  }, [doc]);

  const confirm = async () => {
    try {
      setBusy(true);
      await api.post(`/docs/documents/${doc.id}/acknowledge`);
      toast.success(t("Best\u00e4tigung gespeichert", "Acknowledgement saved"));
      onAcknowledged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="ack-dialog">
        {doc && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{doc.title}</DialogTitle>
              <DialogDescription>Version {doc.version} {"\u00b7"} {doc.description}</DialogDescription>
            </DialogHeader>
            {full?.content ? (
              <div className="rounded-[12px] border border-border bg-muted/30 p-5 max-h-[45vh] overflow-y-auto">
                <DocContentView content={full.content} />
              </div>
            ) : full?.file_id ? (
              <Button variant="outline" className="rounded-[10px] w-fit" onClick={() => downloadApiFile(`/files/${full.file_id}/download`, doc.title)} data-testid="ack-file-download">
                <Download className="h-4 w-4 mr-1.5" /> {t("Dokument herunterladen (PDF)", "Download document (PDF)")}
              </Button>
            ) : (
              <Skeleton className="h-40 rounded-[12px]" />
            )}
            <label className="flex items-start gap-3 rounded-[12px] border border-border p-4 cursor-pointer hover:bg-muted/40 transition-colors">
              <Checkbox checked={read} onCheckedChange={(v) => setRead(!!v)} className="mt-0.5" data-testid="ack-read-checkbox" />
              <span className="text-sm">{t("Ich habe dieses Dokument gelesen und verstanden.", "I have read and understood this document.")}</span>
            </label>
            <DialogFooter>
              <Button className="rounded-[10px] bg-[#0B1220]" disabled={!read || busy} onClick={confirm} data-testid="ack-confirm-button">
                <ShieldCheck className="h-4 w-4 mr-1.5" /> {t("Verbindlich best\u00e4tigen", "Confirm bindingly")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- checklist fill dialog ---------- */
function ChecklistDialog({ checklist, onClose, onSaved }) {
  const { t } = useLang();
  const [state, setState] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef({});
  const completed = checklist?.my_submission?.status === "completed";

  useEffect(() => {
    if (!checklist) return;
    const init = {};
    (checklist.my_submission?.items || []).forEach((i) => { init[i.item_id] = i; });
    setState(init);
  }, [checklist]);

  if (!checklist) return null;

  const setItem = (itemId, patch) => setState((s) => ({ ...s, [itemId]: { ...(s[itemId] || { item_id: itemId }), item_id: itemId, ...patch } }));

  const uploadEvidence = async (itemId, file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("area", "compliance");
    form.append("description", `Nachweis: ${checklist.title}`);
    try {
      const res = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setItem(itemId, { evidence_file_id: res.data.id, evidence_file_name: res.data.filename });
      toast.success(t("Nachweis hochgeladen", "Evidence uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Upload fehlgeschlagen", "Upload failed"));
    }
  };

  const save = async (thenComplete = false) => {
    try {
      setBusy(true);
      await api.put(`/docs/checklists/${checklist.id}/submission`, { items: Object.values(state) });
      if (thenComplete) {
        await api.post(`/docs/checklists/${checklist.id}/submission/complete`);
        toast.success(t("Checkliste abgeschlossen", "Checklist completed"));
      } else {
        toast.success(t("Zwischenstand gespeichert", "Progress saved"));
      }
      onSaved();
      if (thenComplete) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!checklist} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{checklist.title}</DialogTitle>
          <DialogDescription>{checklist.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          {(checklist.items || []).map((item) => {
            const st = state[item.id] || {};
            return (
              <div key={item.id} className={`rounded-[12px] border p-4 space-y-2 ${st.checked ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`} data-testid={`fill-item-${item.id}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={!!st.checked} disabled={completed} onCheckedChange={(c) => setItem(item.id, { checked: !!c })} className="mt-0.5" data-testid={`fill-check-${item.id}`} />
                  <span className="text-sm flex-1">
                    {item.label}
                    {item.required !== false && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#A8905A] font-semibold">Pflicht</span>}
                    {item.needs_evidence && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-600 font-semibold">{t("Nachweis erforderlich", "Evidence required")}</span>}
                  </span>
                </label>
                {!completed && (
                  <div className="flex flex-wrap items-center gap-2 pl-7">
                    <Input
                      value={st.comment || ""}
                      onChange={(e) => setItem(item.id, { comment: e.target.value })}
                      placeholder={t("Kommentar (optional)", "Comment (optional)")}
                      className="rounded-[8px] h-8 text-xs max-w-xs"
                      data-testid={`fill-comment-${item.id}`}
                    />
                    {item.needs_evidence && (
                      <>
                        <input type="file" className="hidden" ref={(el) => (fileRefs.current[item.id] = el)} onChange={(e) => e.target.files?.[0] && uploadEvidence(item.id, e.target.files[0])} />
                        <Button type="button" size="sm" variant="outline" className="rounded-[8px] h-8 text-xs" onClick={() => fileRefs.current[item.id]?.click()} data-testid={`fill-evidence-${item.id}`}>
                          <Upload className="h-3 w-3 mr-1" /> {st.evidence_file_id ? t("Nachweis ersetzen", "Replace evidence") : t("Nachweis hochladen", "Upload evidence")}
                        </Button>
                        {st.evidence_file_name && <span className="text-[11px] text-muted-foreground truncate max-w-[140px]">{st.evidence_file_name}</span>}
                      </>
                    )}
                  </div>
                )}
                {completed && st.comment && <p className="pl-7 text-xs text-muted-foreground">{st.comment}</p>}
              </div>
            );
          })}
        </div>
        {!completed && (
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-[10px]" disabled={busy} onClick={() => save(false)} data-testid="checklist-save-progress">
              {t("Zwischenstand speichern", "Save progress")}
            </Button>
            <Button className="rounded-[10px] bg-[#0B1220]" disabled={busy} onClick={() => save(true)} data-testid="checklist-complete-button">
              <CircleCheck className="h-4 w-4 mr-1.5" /> {t("Abschlie\u00dfen", "Complete")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- page ---------- */
export default function Compliance() {
  const { t } = useLang();
  const [acks, setAcks] = useState(null);
  const [checklists, setChecklists] = useState(null);
  const [ackDoc, setAckDoc] = useState(null);
  const [fillChecklist, setFillChecklist] = useState(null);

  const load = useCallback(() => {
    api.get("/docs/acknowledgements/pending").then((r) => setAcks(r.data)).catch(() => setAcks({ pending: [], confirmed: [] }));
    api.get("/docs/checklists").then((r) => setChecklists(r.data)).catch(() => setChecklists([]));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="space-y-8" data-testid="compliance-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Eleven Compliance Center</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("Pflichtdokumente best\u00e4tigen und Checklisten ausf\u00fcllen", "Confirm mandatory documents and complete checklists")}</p>
      </div>

      {/* pending acknowledgements */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
          <FileWarning className="h-4 w-4 text-[#A8905A]" /> {t("Offene Best\u00e4tigungen", "Pending acknowledgements")}
          {acks && acks.pending.length > 0 && <Badge className="rounded-full bg-[#0B1220] text-[10px]">{acks.pending.length}</Badge>}
        </h2>
        {!acks ? (
          <Skeleton className="mt-3 h-28 rounded-[16px]" />
        ) : acks.pending.length === 0 ? (
          <Card className="mt-3 rounded-[16px]"><CardContent className="py-8 text-center text-sm text-muted-foreground">{t("Alles best\u00e4tigt \u2013 keine offenen Pflichtdokumente.", "All confirmed \u2013 no pending documents.")}</CardContent></Card>
        ) : (
          <div className="mt-3 space-y-3" data-testid="pending-acks-list">
            {acks.pending.map((d) => (
              <Card key={d.id} className="rounded-[14px] border-[#D6C08D]/40" data-testid={`pending-ack-${d.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{d.title}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-full text-[10px]">Version {d.version}</Badge>
                      {d.reack_required && (
                        <Badge variant="outline" className="rounded-full text-[10px] border-amber-400/60 text-amber-700">{t("Neue Version \u2013 erneute Best\u00e4tigung n\u00f6tig", "New version \u2013 re-confirmation required")}</Badge>
                      )}
                    </div>
                  </div>
                  <Button size="sm" className="rounded-[10px] bg-[#0B1220] shrink-0" onClick={() => setAckDoc(d)} data-testid={`ack-open-${d.id}`}>
                    {t("Lesen & best\u00e4tigen", "Read & confirm")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {acks && acks.confirmed.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("Best\u00e4tigt", "Confirmed")}</p>
            <div className="mt-2 space-y-1.5" data-testid="confirmed-acks-list">
              {acks.confirmed.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-[10px] border border-border px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-emerald-600" /> {d.title}</span>
                  <span className="text-xs text-muted-foreground">Version {d.version}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* checklists */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#A8905A]" /> {t("Meine Checklisten", "My checklists")}
        </h2>
        {!checklists ? (
          <Skeleton className="mt-3 h-40 rounded-[16px]" />
        ) : checklists.length === 0 ? (
          <Card className="mt-3 rounded-[16px]"><CardContent className="py-8 text-center text-sm text-muted-foreground">{t("Keine Checklisten zugewiesen.", "No checklists assigned.")}</CardContent></Card>
        ) : (
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="my-checklists">
            {checklists.map((c) => {
              const sub = c.my_submission;
              const checkedCount = (sub?.items || []).filter((i) => i.checked).length;
              const pct = c.items?.length ? Math.round((checkedCount / c.items.length) * 100) : 0;
              const done = sub?.status === "completed";
              return (
                <Card key={c.id} className="rounded-[16px]" data-testid={`checklist-card-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{c.title}</p>
                      <Badge variant={done ? "default" : "outline"} className={`rounded-full text-[10px] shrink-0 ${done ? "bg-emerald-600" : ""}`}>
                        {done ? t("Abgeschlossen", "Completed") : sub ? t("In Bearbeitung", "In progress") : t("Offen", "Open")}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{c.description}</p>
                    <div className="mt-3 flex items-center gap-3">
                      <Progress value={done ? 100 : pct} className="h-1.5 flex-1" />
                      <span className="font-mono-num text-xs text-muted-foreground">{done ? 100 : pct}%</span>
                    </div>
                    {c.due_days && (
                      <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {t("Frist", "Due")}: {c.due_days} {t("Tage", "days")}</p>
                    )}
                    <Button size="sm" variant={done ? "outline" : "default"} className={`mt-4 rounded-[10px] w-full ${done ? "" : "bg-[#0B1220]"}`} onClick={() => setFillChecklist(c)} data-testid={`checklist-open-${c.id}`}>
                      {done ? t("Ansehen", "View") : t("Ausf\u00fcllen", "Fill in")}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <AckDialog doc={ackDoc} onClose={() => setAckDoc(null)} onAcknowledged={load} />
      <ChecklistDialog checklist={fillChecklist} onClose={() => setFillChecklist(null)} onSaved={load} />
    </div>
  );
}
